import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductState, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { canAddToCart } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseDto } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToListLink from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand from "@insite/content-library/Components/ProductBrand";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import Block from "@store/Components/Common/Block";
import ProductFulfillment from "@store/Components/Store/Product/ProductFulfillment";
import StoreAnchorLink, { StoreAnchorProps } from "@store/Components/Store/StoreAnchorLink";
import StoreAnchorMenu from "@store/Components/Store/StoreAnchorMenu";
import StoreConfigurationTable from "@store/Components/Store/StoreConfigurationTable";
import StoreObsoleteSupercededProductLabel from "@store/Components/Store/StoreObsoleteSupercededProductLabel";
import StoreProductDetailsAvailability from "@store/Components/Store/StoreProductDetailsAvailability";
import styles from "@store/Components/Store/StoreProductDetailsView/styles";
import StoreProductIsStockedMessage from "@store/Components/Store/StoreProductIsStockedMessage";
import StoreSchematicsView from "@store/Components/Store/StoreSchematicsView";
import StoreWholegoodsView from "@store/Components/Store/StoreSchematicsView/StoreWholegoodsView";
import { SMALLEST_HEADER_HEIGHT } from "@store/Overrides/Widgets/Header/LogoLinksSearch";
import ProductDetailsAddToCartButton from "@store/Overrides/Widgets/ProductDetails/ProductDetailsAddToCartButton";
import { getStoreSession } from "@store/Store/Context/ContextSelectors";
import { getHasRelatedProducts } from "@store/Store/Pages/ProductDetails/ProductDetailsSelectors";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import getBooleanProperty from "@store/utilities/getBooleanProperty";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import parseNumber from "@store/utilities/parseNumber";
import { InfosBoxType } from "@store/Widgets/InfosBox";
import parse from "html-react-parser";
import React, {useEffect, useState } from 'react'
import { connect } from "react-redux";
import styled from "styled-components";

const PageTitle = getWidgetComponent("ProductDetails/ProductDetailsPageTitle");
const PartNumber = getWidgetComponent("ProductDetails/ProductDetailsPartNumber");
const ManufacturingPartNumber = getWidgetComponent("ProductDetails/ProductDetailsManufacturingPartNumber");
const PrimaryImage = getWidgetComponent("ProductDetails/ProductDetailsPrimaryImage");
const Price = getWidgetComponent("ProductDetails/ProductDetailsPrice");
const Description = getWidgetComponent("ProductDetails/ProductDetailsDescription");
const FeaturedBullets = getWidgetComponent("ProductDetails/ProductDetailsFeaturedBullets");
const Documents = getWidgetComponent("ProductDetails/ProductDetailsDocuments");
const ImageCarousel = getWidgetComponent("ProductDetails/ProductDetailsImageCarousel");
const Attributes = getWidgetComponent("ProductDetails/ProductDetailsAttributes");
const UnitOfMeasureSelect = getWidgetComponent("ProductDetails/ProductDetailsUnitOfMeasureSelect");
const QuantityOrdered = getWidgetComponent("ProductDetails/ProductDetailsQuantityOrdered");
const Availability = getWidgetComponent("ProductDetails/ProductDetailsAvailability");
const InfosBox = getWidgetComponent("InfosBox");

type OwnProps = WidgetProps & HasProductContext;
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: StoreApplicationState, ownProps: OwnProps) => {
    const session = getStoreSession(state);
    const productState = getProductState(
        state,
        ownProps.productContext.product.detail?.replacementProductId ?? undefined,
    );
    return {
        canAddToCart: canAddToCart(state, ownProps.productContext.product),
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
        supercededProduct: productState.value,
        supercededProductIsLoading: productState.isLoading,
        hasRelatedProducts: getHasRelatedProducts(state),
        fulfillmentMethod: session.fulfillmentMethod,
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        hasEnoughInventory: ownProps.productContext ? hasEnoughInventory(state, ownProps.productContext) : false,
        productSettings: getSettingsCollection(state).productSettings,
    };
};


const Anchor = styled.span`
    position: absolute;
    left: 0;
    height: 0;
    opacity: 0;
    ${props =>
        breakpointValuesMediaQueries(
            props.theme,
            [{ values: [null, null, -SMALLEST_HEADER_HEIGHT - 96, -SMALLEST_HEADER_HEIGHT - 80], properties: ["top"], unit: "px" }],
            mediaOptions.min,
        )}
`;

export const availabilityAnchorLink = "branch-availability";

const StoreProductDetailsView: React.FC<Props> = ({
    productContext,
    canAddToCart,
    isSignedIn,
    supercededProduct,
    supercededProductIsLoading,
    hasRelatedProducts,
    fulfillmentMethod,
    pickUpWarehouse,
    hasEnoughInventory,
    productSettings,
    ...props
}) => {
    const { product, productInfo } = productContext;
    const { unitOfMeasure, inventory } = productInfo;

    const uom = inventory?.inventoryWarehousesDtos?.find(uom => uom.unitOfMeasure === unitOfMeasure);
    const currentWarehouseInventory =
        uom?.warehouseDtos?.find(o => o.name === pickUpWarehouse?.name);

    const tabAnchors: Array<StoreAnchorProps> = [];
    product.specifications?.forEach(x => {
        tabAnchors.push({ label: x.nameDisplay, anchor: x.name });
    });

    const configurationAnchors: Array<StoreAnchorProps> = [];
    product.detail?.configuration?.configSections?.forEach(x => {
        if (x.sectionOptions && x.sectionOptions.length > 0) {
            configurationAnchors.push({
                label: `${x.sectionName}_ConfigurationTitle`,
                anchor: `${x.sectionName}-configuration`,
            });
        }
    });

    const anchors: Array<StoreAnchorProps> = [
        { label: "Description & Documents", anchor: "description" },
        ...configurationAnchors,
        { label: "Branch Availability", anchor: availabilityAnchorLink },
        { label: "Specifications", anchor: "specifications" },
        { label: "Parts & Schematics", anchor: "parts-schematics" },
        { label: "Wholegoods", anchor: "wholegoods" },
        ...tabAnchors,
        ...(hasRelatedProducts ? [{ label: "Related Products", anchor: "related" }] : []),
    ];

    const showProp65 = getBooleanProperty(product.properties.prop65);
    const showFeaturedBullets = product.properties.featuredBullets?.length > 0;
    const isHighDemandMode = getBooleanProperty(productContext?.product?.properties?.isHighDemandMode);
    const parsedQtyLimit = parseNumber(productContext?.product?.properties?.qtyLimit);
    const qtyLimit = parsedQtyLimit || -1;
    const highDemandTextWithQty = "This item is in high demand, a maximum of {0} can be ordered at a time.";
    const highDemandTextNoQty = "This item is in high demand, quantities are limited.";
    const highDemandMessage = `${translate(
        qtyLimit >= 0 ? highDemandTextWithQty : highDemandTextNoQty,
        `${qtyLimit}`,
    )}`;

    const isStocked = getBooleanProperty(product.properties.isStocked);
    
    const [OpenProductHeader, setOpenProductHeader] = useState(false);
    const handleScroll = () => {
        const anchorSection = document.querySelector("[data-test-selector='anchorSection']");

        if (!anchorSection) {
            return;
        }

        const scrollPosition = window.scrollY;
        const anchorPosition = anchorSection.getBoundingClientRect().top + scrollPosition;
        setOpenProductHeader(false);

        if (scrollPosition >= (anchorPosition - 60)) {
            setOpenProductHeader(true);
        }
    }

    const [pickupQty, setPickupQty] = useState<number | undefined>(undefined);
    const [shipQty, setShipQty] = useState<number | undefined>(undefined);
    const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
    const [nearbyWarehouse, setNearbyWarehouse] = useState<WarehouseDto>();

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    });

    useEffect(() => {
        if (pickUpWarehouse) {
            if (currentWarehouseInventory) {
                setPickupQty(currentWarehouseInventory.qty);
                setSelectedWarehouse(currentWarehouseInventory.name);
            }

            if (currentWarehouseInventory && currentWarehouseInventory.qty === 0) {
                const nearbyPickup = uom?.warehouseDtos?.find((warehouseInventory) => warehouseInventory.qty > 0);

                if (nearbyPickup) {
                    setNearbyWarehouse(nearbyPickup);
                }
            }
        }

        if (inventory?.qtyOnHand !== undefined) {
            setShipQty(inventory?.qtyOnHand)
        }

    }, [inventory, unitOfMeasure]);

    const [selectedFulfillmentMethod, setSelectedFulfillmentMethod] = useState<string>(fulfillmentMethod);

    return (
        <>
            <Block {...styles.headingBlock}>
                {product.brand && <ProductBrand brand={product.brand} />}
                <PageTitle />
                <StyledWrapper {...styles.headingNumbers}>
                    <PartNumber />
                    <ManufacturingPartNumber />
                </StyledWrapper>
            </Block>
            <Block {...styles.detailBlock}>
                <GridContainer {...styles.detailGrid}>
                    <GridItem {...styles.detailImageGridItem}>
                        <StyledWrapper {...styles.detailImageWrapper}>
                            <StyledWrapper {...styles.detailImage}>
                                <PrimaryImage />
                            </StyledWrapper>
                            <ImageCarousel />
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.detailContentGridItem}>
                        {isSignedIn && (
                            <>
                                <StyledWrapper {...styles.prodInfosGrid}>
                                    <StyledWrapper {...styles.prodInfosGridItem}>
                                        <Price />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.prodInfosGridItemRightBottom}>
                                        <Availability />
                                    </StyledWrapper>
                                    {(canAddToCart || product.canAddToWishlist) && (
                                        <>
                                            <StyledWrapper {...styles.prodInfosGridItem}>
                                                <StyledWrapper {...styles.detailQtyInnerWrapper}>
                                                    {!OpenProductHeader && (
                                                        <>
                                                            <QuantityOrdered />
                                                            <UnitOfMeasureSelect />
                                                        </>
                                                    )}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.prodInfosGridItemRight}>
                                                <ProductAddToListLink extendedStyles={styles.addToListLink} />
                                            </StyledWrapper>
                                        </>
                                    )}
                                </StyledWrapper>
                                {productSettings.canAddToCart && hasEnoughInventory && canAddToCart ? (
                                    <StyledWrapper {...styles.prodDetailsFulfillment}>
                                        <ProductFulfillment
                                            product={productContext}
                                            pickupQty={pickupQty}
                                            shipQty={shipQty}
                                            isHighDemandMode={isHighDemandMode}
                                            nearbyWarehouse={nearbyWarehouse}
                                            changeWarehouse={(selectedWarehouse) => setSelectedWarehouse(selectedWarehouse)}
                                            selectedFulfillmentMethod={selectedFulfillmentMethod}
                                            changeFulfillmentMethod={(requestedFulfillmentMethod) => setSelectedFulfillmentMethod(requestedFulfillmentMethod)}
                                        />
                                    </StyledWrapper>
                                ) : (
                                    <StoreObsoleteSupercededProductLabel />
                                )}
                                {canAddToCart && (
                                    <StyledWrapper {...styles.detailActionsInnerWrapper}>
                                        <StyledWrapper {...styles.detailActionWrapper}>
                                            {!OpenProductHeader && (
                                                <ProductDetailsAddToCartButton
                                                    warehouse={selectedWarehouse}
                                                    requestedFulfillmentMethod={selectedFulfillmentMethod}
                                                />
                                            )}
                                        </StyledWrapper>
                                        {isHighDemandMode && (
                                            <StyledWrapper {...styles.detailActionsInnerWrapper}>
                                                <StyledWrapper {...styles.detailActionWrapper}>
                                                    <Typography {...styles.highDemandModeText}>
                                                        {highDemandMessage}
                                                    </Typography>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                )}
                            </>
                        )}
                        <StoreProductIsStockedMessage
                            isStocked={isStocked}
                            qtyOrdered={productContext.productInfo.qtyOrdered}
                            qtyOnHand={currentWarehouseInventory?.qty}
                        />
                        {product.properties.shortDescription && (
                            <>
                                <Typography {...styles.detailContentDescription}>
                                    {product.properties.shortDescription}
                                </Typography>
                                <StoreAnchorLink
                                    anchor="description"
                                    label={translate("See full description")}
                                    {...styles.detailContentMoreLink}
                                />
                            </>
                        )}
                    </GridItem>
                </GridContainer>
            </Block>
            <Block {...styles.anchorBlock} data-test-selector="anchorSection">
                <StoreAnchorMenu links={anchors} />
            </Block>
            <Block {...OpenProductHeader ? styles.popoutHeadingBlock : styles.hiddenPopoutHeadingBlock}>
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.container}>
                        <GridContainer {...styles.tableGridHead}>
                            <GridItem {...styles.headingNumbersGridHeader}>
                                <StyledWrapper {...styles.headingNumbers}>
                                    {product.brand && <ProductBrand brand={product.brand} />}
                                    <PageTitle />
                                    <PartNumber />
                                    <ManufacturingPartNumber />
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.detailQtyInnerWrapperGridHeader}>
                                <QuantityOrdered />
                                <UnitOfMeasureSelect />
                            </GridItem>
                            <GridItem {...styles.detailActionWrapperGridHeader}>
                                <ProductDetailsAddToCartButton requestedFulfillmentMethod={selectedFulfillmentMethod} />
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </StyledWrapper>
            </Block>

            <Block {...styles.infoBlock}>
                <GridContainer {...styles.infoGrid}>
                    <GridItem {...styles.infoDescriptionGridItem}>
                        <Typography {...styles.sectionTitle}>
                            {translate("Description")}
                            <Anchor id="description" aria-hidden="true">anchor</Anchor>
                        </Typography>
                        <Description />
                    </GridItem>
                    <GridItem {...styles.infoDocumentsGridItem}>
                        <Typography {...styles.sectionTitle}>
                            {translate("Documents")}
                            <Anchor id="documents" aria-hidden="true">anchor</Anchor>
                        </Typography>
                        <Documents />
                    </GridItem>
                </GridContainer>
                {showFeaturedBullets && 
                    <>
                        <Typography {...styles.sectionTitle}>
                                {translate("Feature Bullets")}
                                <Anchor id="feature-bullets" aria-hidden="true">anchor</Anchor>
                        </Typography>
                        <FeaturedBullets />
                    </>
                }
            </Block>
            {product.detail?.configuration?.configSections?.map(configSection => {
                if (!configSection.sectionOptions || configSection.sectionOptions.length === 0) {
                    return null;
                }

                return (
                    <Block {...styles.configurationBlock} key={configSection.sectionName}>
                        <Typography id={`${configSection.sectionName}-configuration`} {...styles.sectionTitle}>
                            {translate(`${configSection.sectionName}_ConfigurationTitle`)}
                        </Typography>
                        <StoreConfigurationTable configSection={configSection} />
                    </Block>
                );
            })}
            <Block {...styles.branchAvailability}>
                <Typography {...styles.sectionTitle}>
                    {translate("Branch Availability")}
                    <Anchor id="branch-availability" aria-hidden="true">anchor</Anchor>
                </Typography>
                <StoreProductDetailsAvailability productContext={productContext} />
            </Block>
            <Block {...styles.attributesBlock}>
                <Typography {...styles.sectionTitle}>
                    {translate("Specifications")}
                    <Anchor id="specifications" aria-hidden="true">anchor</Anchor>
                </Typography>
                <Attributes />
            </Block>
            {showProp65 && props.fields.prop65 && (
                <Block {...styles.attributeBlock}>
                    <InfosBox type={InfosBoxType.Warning} content={parse(props.fields.prop65, parserOptions)} />
                </Block>
            )}
            <Block {...styles.schematicsBlock}>
                <Typography {...styles.sectionTitle}>
                    {translate("Parts & Schematics")}
                    <Anchor id="parts-schematics" aria-hidden="true">anchor</Anchor>
                </Typography>
                <StoreSchematicsView />
            </Block>
            <Block {...styles.schematicsBlock}>
                <Typography {...styles.sectionTitle}>
                    {translate("Wholegoods")}
                    <Anchor id="wholegoods" aria-hidden="true">anchor</Anchor>
                </Typography>
                <StoreWholegoodsView />
            </Block>
            {product.specifications?.map(tab => (
                <Block key={tab.id} {...styles.tabBlock}>
                    <Typography id={tab.name} {...styles.sectionTitle}>
                        {tab.nameDisplay}
                    </Typography>
                    {parse(tab.htmlContent, parserOptions)}
                </Block>
            ))}
            <Block {...styles.relatedBlock}>
                <Anchor id="related" aria-hidden="true">anchor</Anchor>
                <Zone contentId={props.id} zoneName="RelatedProduct" />
            </Block>
        </>
    );
};

export default withProductContext(connect(mapStateToProps)(StoreProductDetailsView));

